import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { IApiError, TAppError } from '@/store/types/base.state';
import { TAppState } from '@/store/reducers/reducers';
import { RolesActionValues, TRolesActions } from '@/features/roles/store/types/roles.types';
import {
  IAdvancedRole,
  IAdvancedRoleMetadata,
  isAdvancedRoleSlug,
  TAdvancedRoleStatus,
} from '@/features/roles/store/models/advancedRole.model';
import { ISupportUser } from '@/features/dashboard-users/store/support-user.model';

import {
  TSupportUserAction,
  IUpdateSupportUserSuccessAction,
  SupportUserActionTypes,
} from '@/features/dashboard-users/store/types/support-user.types';

import { RoleServices } from '@/services/RoleServices';
import { SupportUserService } from '@/features/dashboard-users/services/SupportUserService';

export function getAdvancedRole(roleSlug: string): ThunkAction<Promise<void>, TAppState, {}, TRolesActions> {
  const request = (): TRolesActions => ({
    type: RolesActionValues.GET_ROLE,
  });

  const fail = (error: TAppError): TRolesActions => ({
    type: RolesActionValues.GET_ROLE_FAILED,
    error: error as IApiError,
  });

  const success = (role: IAdvancedRole): TRolesActions => ({
    type: RolesActionValues.GET_ROLE_SUCCESS,
    shouldSkipToaster: true,
    role,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TRolesActions>): Promise<void> => {
    dispatch(request());

    try {
      if (isAdvancedRoleSlug(roleSlug)) {
        const roles = await RoleServices.GetRole(roleSlug);
        dispatch(success(roles));
      } else {
        throw new Error(`This role doesn't exist`);
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export function updateAdvancedRole(
  roleSlug: string,
  userId: string,
  status: TAdvancedRoleStatus,
  modifiedBy: string,
): ThunkAction<Promise<void>, TAppState, {}, TRolesActions | TSupportUserAction> {
  const request = (): TRolesActions => ({
    type: RolesActionValues.UPDATE_ROLE,
  });

  const fail = (error: TAppError): TRolesActions => ({
    type: RolesActionValues.UPDATE_ROLE_FAILED,
    error: error as IApiError,
  });

  const success = (supportUser: ISupportUser): IUpdateSupportUserSuccessAction => ({
    type: SupportUserActionTypes.UPDATE_SUPPORT_USER_SUCCESS,
    supportUser,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TRolesActions | TSupportUserAction>): Promise<void> => {
    dispatch(request());

    try {
      if (isAdvancedRoleSlug(roleSlug)) {
        const supportUser = await SupportUserService.updateSupportUserMetadata(userId, {
          role: { slug: roleSlug, status, modifiedBy, lastModified: Date.now().toString() },
        });
        dispatch(success(supportUser));
      } else {
        throw new Error(`This role doesn't exists`);
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
}

export function setAdvancedRole(
  roleSlug: string,
  status: TAdvancedRoleStatus,
  modifiedBy: string,
): ThunkAction<Promise<void>, TAppState, {}, TRolesActions | TSupportUserAction> {
  const request = (): TRolesActions => ({
    type: RolesActionValues.SET_ROLE,
  });

  const fail = (error: TAppError): TRolesActions => ({
    type: RolesActionValues.SET_ROLE_FAILED,
    error: error as IApiError,
  });

  const success = (role: IAdvancedRoleMetadata): TRolesActions => ({
    type: RolesActionValues.SET_ROLE_SUCCESS,
    shouldSkipToaster: true,
    role,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TRolesActions | TSupportUserAction>): Promise<void> => {
    dispatch(request());

    try {
      if (isAdvancedRoleSlug(roleSlug)) {
        dispatch(
          success({
            slug: roleSlug,
            status,
            modifiedBy,
            lastModified: Date.now().toString(),
          }),
        );
      } else {
        throw new Error(`This role doesn't exists`);
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
}
